import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  AlertCircle,
  Loader2,
  PanelRightClose,
  Check,
  MessageSquareQuote,
  ChevronDown,
} from "lucide-react";
import api from "../../connector/api";

const PRESET_COMMENTS = [
  "Consistently demonstrates excellent understanding and participation in class.",
  "Shows strong analytical thinking and delivers high-quality coursework.",
  "Good overall academic performance. Keep up the consistent effort!",
  "Meets grade expectations, but could benefit from active classroom discussion.",
  "Needs to focus on submitting homework and assignments on schedule.",
  "Requires additional review on core concepts to improve test performance.",
];

const ReportCardTab = ({
  loading,
  error,
  activeClass,
  activeStudents,
  selectedSubjectId,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
  addToast,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [reportCards, setReportCards] = useState({});
  const [activeTerm, setActiveTerm] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [savingIds, setSavingIds] = useState({});
  const [openPresetStudentId, setOpenPresetStudentId] = useState(null);

  const saveTimeoutsRef = useRef({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenPresetStudentId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch active term and report cards
  useEffect(() => {
    const fetchReportCardData = async () => {
      if (!activeClass?.id) return;
      setDataLoading(true);

      try {
        let currentTerm = activeClass?.school_year?.active_term || null;

        if (!currentTerm) {
          const termRes = await api.get("/year-terms/");
          const termsData = Array.isArray(termRes.data)
            ? termRes.data
            : termRes.data.results || [];

          // Find current term from SchoolYearTerm junction or list
          const activeJunction = termsData.find((item) => item.is_current);
          currentTerm = activeJunction ? activeJunction.term : termsData[0]?.term || termsData[0] || null;
        }

        setActiveTerm(currentTerm);

        const params = {
          classroom: activeClass.id,
          term: currentTerm?.id,
        };
        if (selectedSubjectId) params.subject_assignment = selectedSubjectId;

        const cardsRes = await api.get("/gradebook/report-card/", { params });

        const cardsList = Array.isArray(cardsRes.data)
          ? cardsRes.data
          : cardsRes.data.results || [];

        const cardsMap = {};
        cardsList.forEach((card) => {
          cardsMap[String(card.student)] = card;
        });
        setReportCards(cardsMap);
      } catch (err) {
        console.error("Error loading report cards data:", err);
        addToast?.("Could not load report cards data", "error");
      } finally {
        setDataLoading(false);
      }
    };

    fetchReportCardData();
  }, [activeClass?.id, selectedSubjectId, addToast]);

  // Handle comment update and auto-save
  const handleCommentChange = (studentId, studentName, commentText) => {
    const studentKey = String(studentId);

    setReportCards((prev) => ({
      ...prev,
      [studentKey]: {
        ...prev[studentKey],
        teacher_comment: commentText,
      },
    }));

    if (saveTimeoutsRef.current[studentKey]) {
      clearTimeout(saveTimeoutsRef.current[studentKey]);
    }

    setSavingIds((prev) => ({ ...prev, [studentKey]: true }));

    saveTimeoutsRef.current[studentKey] = setTimeout(async () => {
      try {
        const existingCard = reportCards[studentKey];
        const payload = {
          student: studentId,
          classroom: activeClass?.id,
          subject_assignment: selectedSubjectId,
          term: activeTerm?.id,
          teacher_comment: commentText,
        };

        let res;
        if (existingCard?.id) {
          res = await api.patch(`/gradebook/report-card/${existingCard.id}/`, {
            teacher_comment: commentText,
            term: activeTerm?.id,
          });
        } else {
          res = await api.post("/gradebook/report-card/", payload);
        }

        setReportCards((prev) => ({
          ...prev,
          [studentKey]: res.data,
        }));

        addToast?.(`Updated report card for ${studentName}`, "success");
      } catch (err) {
        console.error("Failed to save report card comment:", err);
        addToast?.(`Failed to save comment for ${studentName}`, "error");
      } finally {
        setSavingIds((prev) => ({ ...prev, [studentKey]: false }));
      }
    }, 800);
  };

  const handleSelectPreset = (studentId, fullName, presetText) => {
    const existingComment = reportCards[String(studentId)]?.teacher_comment || "";
    const updatedText = existingComment
      ? `${existingComment} ${presetText}`
      : presetText;

    handleCommentChange(studentId, fullName, updatedText);
    setOpenPresetStudentId(null);
  };

  const filteredStudents = (activeStudents || []).filter((student) => {
    const query = searchTerm.toLowerCase().trim();
    const fullName = `${student.first_name || ""} ${student.last_name || ""}`.toLowerCase();
    const studentId = String(student.student_id_number || student.id || "").toLowerCase();
    return !query || fullName.includes(query) || studentId.includes(query);
  });

  if (loading || dataLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <span className="text-xs font-semibold">Loading student report cards...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 max-w-md">
          <AlertCircle className="w-5 h-5 mx-auto mb-2 text-rose-600" />
          <p className="text-xs font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto w-full">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-zinc-200/80 pb-5">
        <div className="flex items-center gap-2 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search student name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-medium placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all shadow-xs"
            />
          </div>

          <div className="px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600">
            Term: {activeTerm?.name || "Active Term"}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          {!isRightSidebarOpen && (
            <button
              type="button"
              onClick={() => setIsRightSidebarOpen?.(true)}
              className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 rounded-lg text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0 cursor-pointer shadow-xs"
            >
              <PanelRightClose className="w-4 h-4 text-zinc-600" />
              <span>Summary</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-xs overflow-hidden">
        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-wider">
                <th className="py-3 px-4 w-64 bg-white">Student Name</th>
                <th className="py-3 px-4 min-w-[360px]">Teacher Remarks & Feedback</th>
                <th className="py-3 px-4 w-32 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 text-xs font-medium text-zinc-700">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student, idx) => {
                  const studentKey = String(student.id);
                  const card = reportCards[studentKey] || {};
                  const isSaving = savingIds[studentKey];
                  const fullName = `${student.first_name} ${student.last_name}`;

                  return (
                    <tr key={student.id} className="hover:bg-zinc-50/60 transition-colors">
                      <td className="py-3 px-4 font-semibold text-zinc-900 align-top">
                        <div className="pt-1">
                          {idx + 1}) {fullName}
                        </div>
                      </td>

                      <td className="py-3 px-4 align-top relative">
                        <div className="relative group">
                          <textarea
                            rows={2}
                            value={card.teacher_comment || ""}
                            placeholder={`Enter remarks for ${student.first_name}...`}
                            onChange={(e) =>
                              handleCommentChange(student.id, fullName, e.target.value)
                            }
                            className="w-full p-2.5 pr-9 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-normal text-zinc-800 placeholder-zinc-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 transition-all resize-y"
                          />

                          <div className="absolute right-2 top-2.5 flex items-center">
                            <button
                              type="button"
                              onClick={() =>
                                setOpenPresetStudentId(
                                  openPresetStudentId === student.id ? null : student.id
                                )
                              }
                              title="Insert predefined remark"
                              className="p-1 rounded-md text-zinc-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                            >
                              <MessageSquareQuote className="w-4 h-4" />
                            </button>
                          </div>

                          {openPresetStudentId === student.id && (
                            <div
                              ref={dropdownRef}
                              className="absolute right-0 top-10 z-30 w-72 bg-white border border-zinc-200 rounded-lg shadow-xl p-1.5 space-y-1 animate-in fade-in zoom-in-95 duration-100"
                            >
                              <div className="px-2 py-1 text-[10px] font-bold text-zinc-400 uppercase tracking-wider flex items-center justify-between border-b border-zinc-100 pb-1 mb-1">
                                <span>Preset Remarks</span>
                                <ChevronDown className="w-3 h-3 text-zinc-400" />
                              </div>
                              <div className="max-h-48 overflow-y-auto custom-scroll space-y-0.5">
                                {PRESET_COMMENTS.map((preset, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    onClick={() =>
                                      handleSelectPreset(student.id, fullName, preset)
                                    }
                                    className="w-full text-left p-2 hover:bg-zinc-50 rounded-lg text-[11px] text-zinc-700 font-normal leading-snug transition-colors cursor-pointer border border-transparent hover:border-zinc-200"
                                  >
                                    "{preset}"
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-center align-middle">
                        {isSaving ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                            <Loader2 className="w-3 h-3 animate-spin" /> Saving
                          </span>
                        ) : card.id ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                            <Check className="w-3 h-3" /> Saved
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-400 italic">No entry</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className="py-12 text-center text-xs text-zinc-400 italic">
                    No matching student records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportCardTab;